import React, { useEffect, useState } from "react";
import { Box, Flex, Text, SimpleGrid, Link, Spinner, Icon } from "@chakra-ui/react";
import { FiBookOpen, FiStar, FiUsers, FiUserPlus, FiGithub } from "react-icons/fi";
import GitHubCalendar from "react-github-calendar";
import { ChapterHeading, Reveal, MotionBox, glass } from "./story";

const USER = "mukulsomukesh";

export default function Stats() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [uRes, rRes] = await Promise.all([
          fetch(`https://api.github.com/users/${USER}`),
          fetch(`https://api.github.com/users/${USER}/repos?per_page=100&sort=updated`),
        ]);
        if (!uRes.ok || !rRes.ok) throw new Error("GitHub API unavailable");
        const user = await uRes.json();
        const repos = await rRes.json();

        const stars = repos.reduce((sum, r) => sum + (r.stargazers_count || 0), 0);

        if (!alive) return;
        setData({
          repos: user.public_repos,
          followers: user.followers,
          following: user.following,
          stars,
        });
        setStatus("ready");
      } catch (e) {
        if (alive) setStatus("error");
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const tiles = data
    ? [
        { icon: FiBookOpen, label: "Repositories", value: data.repos },
        { icon: FiStar, label: "Total Stars", value: data.stars },
        { icon: FiUsers, label: "Followers", value: data.followers },
        { icon: FiUserPlus, label: "Following", value: data.following },
      ]
    : [];

  return (
    <Box id="Stats" py={{ base: "80px", md: "120px" }} px={{ base: "6%", md: "9%" }}>
      <ChapterHeading
        chapter="Chapter 06 · Proof of Work"
        title="Consistency, one commit at a time"
        subtitle="Talk is cheap, so here's the rhythm of the work, straight from GitHub."
      />

      <Box maxW="1000px" mx="auto">
        {/* stat tiles */}
        {status === "loading" && (
          <Flex justify="center" py="40px">
            <Spinner color="#60A5FA" size="lg" thickness="3px" />
          </Flex>
        )}

        {status === "ready" && (
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={{ base: "12px", md: "18px" }} mb="20px">
            {tiles.map((t, i) => (
              <Reveal key={t.label} delay={i * 0.08}>
                <MotionBox
                  whileHover={{ y: -5 }}
                  transition={{ type: "spring", stiffness: 260 }}
                  {...glass({ p: { base: "18px", md: "24px" } })}
                  textAlign="center"
                >
                  <Flex
                    align="center"
                    justify="center"
                    w="46px"
                    h="46px"
                    mx="auto"
                    mb="12px"
                    borderRadius="12px"
                    bg="rgba(96,165,250,0.14)"
                    color="#60A5FA"
                  >
                    <Icon as={t.icon} boxSize="22px" />
                  </Flex>
                  <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="800" color="#f0f2f7" lineHeight="1">
                    {t.value}
                  </Text>
                  <Text fontSize="xs" color="#98a2b8" mt="6px" letterSpacing="0.05em">
                    {t.label}
                  </Text>
                </MotionBox>
              </Reveal>
            ))}
          </SimpleGrid>
        )}

        {/* graceful fallback if the API is rate-limited/unreachable */}
        {status === "error" && (
          <Reveal>
            <Flex
              {...glass({ p: { base: "24px", md: "32px" } })}
              direction="column"
              align="center"
              textAlign="center"
              mb="20px"
              gap="12px"
            >
              <Icon as={FiGithub} boxSize="34px" color="#60A5FA" />
              <Text color="#c2c9d6" fontSize="sm">
                GitHub stats couldn't load right now. Catch the full picture on my profile.
              </Text>
              <Link
                href={`https://github.com/${USER}`}
                isExternal
                bg="#60A5FA"
                color="white"
                fontWeight="700"
                fontSize="sm"
                px="20px"
                py="10px"
                borderRadius="10px"
                _hover={{ bg: "#3B82F6", textDecoration: "none" }}
              >
                View GitHub Profile
              </Link>
            </Flex>
          </Reveal>
        )}

        {/* contribution calendar */}
        <Reveal delay={0.1}>
          <Box {...glass({ p: { base: "18px", md: "24px" } })} overflowX="auto">
            <Text fontSize="sm" fontWeight="700" color="#60A5FA" letterSpacing="0.08em" textTransform="uppercase" mb="16px">
              Contribution graph
            </Text>
            {/* centre the graph so the spare width sits evenly, not all on the right */}
            <Flex justify="center" w="100%">
              <GitHubCalendar
                username={USER}
                blockSize={14}
                blockMargin={5}
                fontSize={15}
                theme={{
                  level0: "#161b22",
                  level1: "#1e3a5f",
                  level2: "#2f5f9e",
                  level3: "#4d8fd6",
                  level4: "#93c5fd",
                }}
              />
            </Flex>
          </Box>
        </Reveal>
      </Box>
    </Box>
  );
}
